import { Injectable } from '@angular/core';
import { ApiService } from '../services/api.service';
import { Observable } from 'rxjs';
import {
  MeasurementUnit,
  RawMaterialCategoryItem,
  RawMaterialEntry,
  RawMaterialItem,
  RawMaterialRequest,
  RawMaterialStock,
  RawMaterialWarehouse,
  StockAdjustment,
  StockAdjustmentFilters,
  StockAdjustmentRequest,
} from './raw-material.model';
import { buildFilterQuery, buildHttpParams } from '../utils';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { PaginatedResponse } from '../common/models/common.model';

@Injectable({
  providedIn: 'root',
})
export class RawMaterialService {
  private readonly baseUrl: string = 'raw-materials/';

  constructor(private apiService: ApiService) {}

  public list(
    filters: {
      category?: string | null;
      company?: string | null;
      search?: string | null;
      ordering?: string | null;
    } = {}
  ): Observable<RawMaterialItem[]> {
    const params: HttpParams | undefined = buildHttpParams(buildFilterQuery(filters));

    return this.apiService.get(this.baseUrl + 'materials', params);
  }

  public post(reqBody: RawMaterialRequest): Observable<RawMaterialItem> {
    return this.apiService.post(this.baseUrl + 'materials/', reqBody);
  }

  public delete_raw_material(id: string): Observable<void> {
    return this.apiService.delete(this.baseUrl + 'materials/' + id + '/');
  }

  public patch(id: string, reqBody: RawMaterialRequest): Observable<RawMaterialItem> {
    return this.apiService.patch(`${this.baseUrl}materials/${id}/`, reqBody);
  }

  public get_warehouses(): Observable<RawMaterialWarehouse[]> {
    return this.apiService.get(this.baseUrl + 'warehouses/');
  }

  public post_warehouse(reqBody: { name: string; address: string }): Observable<RawMaterialWarehouse> {
    return this.apiService.post(this.baseUrl + 'warehouses/', reqBody);
  }

  public get_categories(): Observable<RawMaterialCategoryItem[]> {
    return this.apiService.get(this.baseUrl + 'categories/');
  }

  public post_categories(reqBody: { name: string; short_code: string }): Observable<RawMaterialCategoryItem> {
    return this.apiService.post(this.baseUrl + 'categories/', reqBody);
  }

  public delete_category(id: string): Observable<void> {
    return this.apiService.delete(this.baseUrl + 'categories/' + id + '/');
  }

  public update_category(
    id: string,
    reqBody: { name: string; short_code: string }
  ): Observable<RawMaterialCategoryItem> {
    return this.apiService.patch(this.baseUrl + 'categories/' + id + '/', reqBody);
  }

  public get_units(): Observable<MeasurementUnit[]> {
    return this.apiService.get(this.baseUrl + 'units/');
  }

  public post_unit(name: string, short_name: string): Observable<MeasurementUnit> {
    return this.apiService.post(this.baseUrl + 'units/', { name, short_name });
  }

  public delete_unit(id: string): Observable<void> {
    return this.apiService.delete(this.baseUrl + 'units/' + id + '/');
  }

  public update_warehouse(
    id: string,
    reqBody: Partial<{ name: string; address: string; plant_head_id: string }>
  ): Observable<RawMaterialWarehouse> {
    return this.apiService.patch(this.baseUrl + 'warehouses/' + id + '/', reqBody);
  }

  public delete_warehouse(id: string): Observable<void> {
    return this.apiService.delete(this.baseUrl + 'warehouses/' + id + '/');
  }

  public update_production_manager(id: string, reqBody: { plant_head_id: string }): Observable<RawMaterialWarehouse> {
    return this.apiService.patch(this.baseUrl + 'warehouses/' + id + '/', reqBody);
  }

  public get_stock(q = {}): Observable<PaginatedResponse<RawMaterialStock>> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl + 'stock/', params);
  }

  public get_entries(q = {}): Observable<PaginatedResponse<RawMaterialEntry>> {
    const params = buildHttpParams(q);

    return this.apiService.get(this.baseUrl + 'entries/', params);
  }

  /**
   * Manual stock adjustments. Admin-only (IsAdminUser) on the server, so a non-staff
   * caller gets a 403 rather than an empty list.
   *
   * The collection is append-only by design: there is no update or delete. A wrong
   * adjustment is undone by posting its opposite, which keeps the audit trail intact.
   */
  public create_stock_adjustment(reqBody: StockAdjustmentRequest): Observable<StockAdjustment> {
    return this.apiService.post(this.baseUrl + 'stock-adjustments/', reqBody);
  }

  /** Relation filters take the object's UUID: a name silently returns an empty 200. */
  public get_stock_adjustments(filters: StockAdjustmentFilters = {}): Observable<PaginatedResponse<StockAdjustment>> {
    const params: HttpParams | undefined = buildHttpParams(buildFilterQuery(filters));

    return this.apiService.get(this.baseUrl + 'stock-adjustments/', params);
  }

  public get_stock_adjustment(id: string): Observable<StockAdjustment> {
    return this.apiService.get(`${this.baseUrl}stock-adjustments/${id}/`);
  }

  public export_entries(q = {}): Observable<Blob> {
    const params = buildHttpParams(q);
    return this.apiService.getBlob(this.baseUrl + 'entries/export-entries/', params);
  }

  public export_stock(q = {}): Observable<Blob> {
    const params = buildHttpParams(q);
    return this.apiService.getBlob(this.baseUrl + 'stock/export-inventory/', params);
  }

  /**
   * Observes the full response so the Content-Disposition filename is available and
   * a JSON error body can be told apart from the spreadsheet.
   */
  public export_materials(
    filters: { category?: string | null; company?: string | null; name?: string | null; search?: string | null } = {}
  ): Observable<HttpResponse<Blob>> {
    const params = buildHttpParams(buildFilterQuery(filters));

    return this.apiService.getBlobResponse(this.baseUrl + 'materials/export-materials/', params);
  }
}
